import React, { useContext, useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { UserContext } from "../../components/HomeApp"
import { openAuthDialog } from "../../components/Authentication"
import Header from "../../components/shared/Header/Header"
import Footer from "../../components/shared/Footer/Footer"
import style from "../../components/shared/PageStyle.module.css"

const GLITCH_COLORS = [
  "#ff6363",
  "#00ffff",
  "#ffffff",
  "#ff00ff",
  "#ffff00",
  "#000000",
]

const GLITCH_FRAMES = 28
const GLITCH_FRAME_MS = 50
const GLITCH_PAUSE_MS = 1000

function useGlitchBlackout(active: boolean) {
  const [glitchStyle, setGlitchStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    if (!active) return

    let frame = 0
    let intervalId: ReturnType<typeof setInterval>
    let timeoutId: ReturnType<typeof setTimeout>

    const runGlitch = () => {
      frame = 0
      intervalId = setInterval(() => {
        const rand = () => Math.floor(Math.random() * 100)
        const showImage = Math.random() < 0.2
        const color =
          GLITCH_COLORS[Math.floor(Math.random() * GLITCH_COLORS.length)]
        const skewX = (Math.random() - 0.5) * 30
        const scaleY = 0.85 + Math.random() * 0.3
        setGlitchStyle({
          backgroundColor: showImage ? "black" : color,
          backgroundImage: showImage ? `url(${THEY_LIVE_URL})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `skewX(${skewX}deg) scaleY(${scaleY})`,
          opacity: Math.random() > 0.15 ? 1 : 0,
          clipPath: `inset(${rand()}% 0 ${rand()}% 0)`,
          transition: "none",
        })
        frame++
        if (frame >= GLITCH_FRAMES) {
          clearInterval(intervalId)
          setGlitchStyle({
            backgroundColor: "black",
            backgroundImage: "none",
            transform: "none",
            opacity: 1,
          })
          timeoutId = setTimeout(runGlitch, GLITCH_PAUSE_MS)
        }
      }, GLITCH_FRAME_MS)
    }

    runGlitch()
    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [active])

  return { glitchStyle }
}

// Assembled at runtime to avoid bot scraping
const e = (parts: number[]) => parts.map((c) => String.fromCharCode(c)).join("")
const EMAIL = e([
  106, 111, 110, 64, 98, 97, 110, 100, 110, 97, 100, 97, 46, 99, 111, 109,
])
const PHONE = e([49, 32, 50, 48, 49, 45, 57, 55, 50, 45, 53, 56, 49, 50])

function ContactSection() {
  const [phoneVisible, setPhoneVisible] = useState(false)
  const [emailVisible, setEmailVisible] = useState(false)

  return (
    <div className="mt-16 text-center">
      <p className="text-2xl font-extrabold uppercase mb-6">
        Want to know more?
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-6 text-lg">
        <span>
          {emailVisible ? (
            <span>
              Email us:{" "}
              <a
                href={`mailto:${EMAIL}`}
                className="text-bn-neon-red underline"
              >
                {EMAIL}
              </a>
            </span>
          ) : (
            <button
              onClick={() => setEmailVisible(true)}
              className="underline text-white focus:outline-none"
            >
              Email us
            </button>
          )}
        </span>
        <span>
          {phoneVisible ? (
            <span>
              Call us: <span className="text-bn-neon-red">{PHONE}</span>
            </span>
          ) : (
            <button
              onClick={() => setPhoneVisible(true)}
              className="underline text-white focus:outline-none"
            >
              Call us
            </button>
          )}
        </span>
      </div>
    </div>
  )
}

const COMPANY_PHOTO_URL =
  "https://bandnada.s3.amazonaws.com/static/external_assets/web/bandnada_company_photo_1_w_mask.png"
const THEY_LIVE_URL =
  "https://bandnada.s3.amazonaws.com/static/external_assets/web/bandnada_they_live.png"

const ForVenuesPage: React.FC = () => {
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const [blackingOut, setBlackingOut] = useState(false)
  const { glitchStyle } = useGlitchBlackout(blackingOut)

  useEffect(() => {
    if (window.location.hash !== "#donations") return
    const timer = setTimeout(() => {
      document
        .getElementById("donations")
        ?.scrollIntoView({ behavior: "smooth" })
    }, 600)
    return () => clearTimeout(timer)
  }, [])

  if (blackingOut) {
    return <div style={{ position: "fixed", inset: 0, ...glitchStyle }} />
  }

  return (
    <div className={style.rootElStyle} style={{ backgroundColor: "#2d2d2d" }}>
      <Header user={user} />
      <div className="grid grid-cols-12 w-full px-2 mt-8 pb-12 text-white">
        <div className="col-span-12 lg:col-span-8 lg:col-start-3">
          {/* Introduction */}
          <div>
            <div style={{ height: 300 }}></div>
            <h1 className="uppercase leading-tight pb-3 text-center">
              <span className="font-bold text-2xl block">Want</span>
              <span className="font-extrabold text-4xl block text-bn-neon-red">
                more people
              </span>
              <span className="font-bold text-2xl block">at every one of </span>
              <span className="font-extrabold text-4xl block text-bn-neon-red">
                your shows?
              </span>
            </h1>
            <div style={{ height: 180 }}></div>
            <h2 className="text-xl lg:text-2xl mb-2 text-center">We do too.</h2>
            <img
              src={COMPANY_PHOTO_URL}
              alt="bandNada community"
              className="mt-6 w-full lg:w-3/5 mx-auto block"
            />
          </div>

          {/* How we do it */}
          <p className="text-xl lg:text-4xl bg-bn-neon-red text-white text-black font-extrabold text-center mt-32 px-4 py-8 leading-snug">
            bandNada brings more fans to LOCAL SHOWS by unifying the local music
            community and making it accessible to all of our neighbors.
          </p>

          <div className="mb-10 flex flex-col justify-center mt-32">
            <h3 className="text-center">What is bandNada?</h3>
            <div
              className="text-center px-6 py-8"
              style={{ border: "4px solid #ff6363" }}
            >
              <p className="text-xl lg:text-3xl font-bold uppercase leading-tight mb-0">
                Unification.
              </p>
              <p className="text-xl lg:text-3xl font-bold uppercase leading-tight mb-0">
                Discovery.
              </p>
              <p className="text-xl lg:text-3xl font-bold uppercase leading-tight mb-0">
                Events.
              </p>
              <p className="text-xl lg:text-3xl font-bold uppercase leading-tight mb-0">
                Ticketing.
              </p>
              <p className="text-xl lg:text-3xl font-bold uppercase leading-tight mb-1">
                Owned by you, me, and us only.
              </p>
              <a
                href="/own-bandnada"
                target="_blank"
                rel="noreferrer"
                className="text-bn-neon-red underline text-center italic text-sm block"
              >
                learn more
              </a>
            </div>
          </div>

          {/* Ticketing / Donations */}
          <div className="mt-32" id="donations">
            <h2 className="font-extrabold text-3xl uppercase text-center mb-10">
              Ticketing & Donations
            </h2>
            <div className="max-w-md mx-auto">
              <p className="text-base lg:text-lg mb-4 text-center">
                <span className="font-bold">We are Partners</span>
                <br />
                Post shows anytime from anywhere at any price.
                <br />
                No Predatory Contracts
                <br />
                No Upsells
                <br />
                Our fee structure is simple:
              </p>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="py-2 pr-8 font-bold uppercase text-sm text-gray-400">
                      Ticket Price
                    </th>
                    <th className="py-2 font-bold text-sm text-right text-gray-400">
                      FEE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-700">
                    <td className="py-3 pr-8">&lt;&#61; $5</td>
                    <td className="py-3 font-bold text-bn-neon-red text-right">
                      No fee
                    </td>
                  </tr>
                  <tr className="border-b border-gray-700">
                    <td className="py-3 pr-8">$5 – $15</td>
                    <td className="py-3 text-right">$1 flat fee</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-8">&gt; $15</td>
                    <td className="py-3 text-right">10% fee</td>
                  </tr>
                </tbody>
              </table>

              <table className="w-full text-left mt-8">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="py-2 pr-8 font-bold uppercase text-sm text-gray-400">
                      Donation Amount
                    </th>
                    <th className="py-2 font-bold text-sm text-right text-gray-400">
                      FEE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-700">
                    <td className="py-3 pr-8">$5 – $15</td>
                    <td className="py-3 text-right">$1 flat fee</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-8">&gt; $15</td>
                    <td className="py-3 text-right">10% fee</td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm italic text-center mt-4">
                bandNada ticketing is powered by{" "}
                <a
                  href="https://stripe.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-bn-neon-red underline"
                >
                  Stripe
                </a>
                . We don't store any credit card information.
                <br />
                Payouts are sent direct to your bank account within three days
                of event.
              </p>
            </div>
          </div>

          {/* What do you choose? / Venue Sign Up */}
          <div className="text-center mt-32 flex flex-col items-center px-4">
            <p className="text-2xl font-extrabold uppercase mb-2">
              Freedom of Choice
            </p>

            <img
              src="https://bandnada.s3.amazonaws.com/static/external_assets/web/bandnada_matrix_pills.jpeg"
              alt="Red pill or blue pill"
              className="mt-2 max-w-xs w-full"
            />

            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() =>
                  dispatch(
                    openAuthDialog({
                      modalState: "register",
                      defaultBandType: "venue",
                    }),
                  )
                }
                className="px-8 py-3 font-bold uppercase tracking-wide text-white focus:outline-none"
                style={{ backgroundColor: "#ff6363" }}
              >
                Join Us
              </button>
              <button
                onClick={() => setBlackingOut(true)}
                className="px-8 py-3 font-bold uppercase tracking-wide text-white bg-blue-600 focus:outline-none"
              >
                More of the Same
              </button>
            </div>
          </div>

          {/* Contact */}
          <ContactSection />
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default ForVenuesPage
